/**
 * Momentum tier badge - color-coded pill with optional icon.
 */
import React from 'react';
import { Flame, Snowflake, TrendingUp, Minus, TrendingDown } from 'lucide-react';
import { MomentumTier } from '../types/dealMomentum';

interface Props {
  tier: MomentumTier | string;
  size?: 'sm' | 'md';
  showIcon?: boolean;
  className?: string;
}

const TIER_STYLES: Record<
  string,
  { bg: string; text: string; border: string; label: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  hot: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300',
    label: 'Hot',
    Icon: Flame,
  },
  warming: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-300',
    label: 'Warming',
    Icon: TrendingUp,
  },
  steady: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
    label: 'Steady',
    Icon: Minus,
  },
  cooling: {
    bg: 'bg-sky-100',
    text: 'text-sky-700',
    border: 'border-sky-300',
    label: 'Cooling',
    Icon: TrendingDown,
  },
  cold: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-300',
    label: 'Cold',
    Icon: Snowflake,
  },
};

export function momentumTierColorClasses(tier: string): string {
  const s = TIER_STYLES[tier];
  if (!s) return 'bg-gray-100 text-gray-700 border-gray-300';
  return `${s.bg} ${s.text} ${s.border}`;
}

export function momentumTierLabel(tier: string): string {
  return (
    TIER_STYLES[tier]?.label ||
    tier.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const MomentumBadge: React.FC<Props> = ({
  tier,
  size = 'md',
  showIcon = true,
  className = '',
}) => {
  const style = TIER_STYLES[tier];
  const Icon = style?.Icon;
  const sizeClasses =
    size === 'sm' ? 'px-2 py-0.5 text-[11px] gap-1' : 'px-2.5 py-1 text-xs gap-1.5';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${sizeClasses} ${momentumTierColorClasses(
        tier
      )} ${className}`}
    >
      {showIcon && Icon && <Icon className={iconSize} />}
      {momentumTierLabel(tier)}
    </span>
  );
};

export default MomentumBadge;
